import React, { useState } from 'react';
import { BIO_DATA } from '../utils/bioCalculators';

export default function Biochemistry() {
  const [species, setSpecies] = useState('dog');
  const [values, setValues] = useState({});
  const [report, setReport] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  const handleInput = (key, val) => setValues({ ...values, [key]: val });

  // ----- Durum hesaplama (Düşük / Normal / Yüksek) -----
  const getStatus = (key, value) => {
    if (!value || value === '') return undefined;
    const num = parseFloat(value);
    if (isNaN(num)) return undefined;

    const data = BIO_DATA[species]?.[key];
    if (data) {
      const [min, max] = data.range;
      if (num < min) return 'Düşük';
      if (num > max) return 'Yüksek';
      return 'Normal';
    }

    // Özel hesaplanan A/G oranı
    if (key === 'A/G') {
      const alb = parseFloat(values.ALB);
      const tp = parseFloat(values.TP);
      if (isNaN(alb) || isNaN(tp) || tp <= alb) return undefined;
      const ratio = alb / (tp - alb);
      if (species === 'cat') {
        if (ratio < 0.7) return 'Düşük';
        if (ratio > 2.0) return 'Yüksek';
        return 'Normal';
      } else {
        if (ratio < 0.8) return 'Düşük';
        if (ratio > 2.5) return 'Yüksek';
        return 'Normal';
      }
    }
    return undefined;
  };

  const getStatuses = () => {
    const statuses = {};
    Object.keys(BIO_DATA[species]).forEach((key) => {
      const status = getStatus(key, values[key]);
      if (status) statuses[key] = status;
    });
    const agStatus = getStatus('A/G');
    if (agStatus) statuses['A/G'] = agStatus;
    return statuses;
  };

  // ----- Profiller için gerekli parametreler -----
  const requiredParams = {
    liver: ['ALT', 'AST', 'ALB', 'ALP', 'GGT', 'TBIL', 'CHOL', 'TG'],
    kidney: ['BUN', 'CREA', 'CA', 'PHOS'],
    digestive: ['LDH'],
    viral: ['ALB', 'TP'],
  };

  const generateSuggestions = () => {
    const missing = [];
    const activeProfiles = ['liver', 'kidney', 'digestive', 'viral'];

    activeProfiles.forEach(profile => {
      const needed = requiredParams[profile] || [];
      needed.forEach(key => {
        if (!values[key] || values[key].trim() === '') {
          if (!missing.includes(key)) missing.push(key);
        }
      });
    });

    const suggestionMessages = missing.map(key => {
      const turkceKey = {
        ALT: 'ALT', AST: 'AST', ALB: 'Albumin', ALP: 'ALP', GGT: 'GGT',
        TBIL: 'Total Bilirubin', CHOL: 'Kolesterol', TG: 'Trigliserid',
        BUN: 'BUN', CREA: 'Kreatinin', CA: 'Kalsiyum', PHOS: 'Fosfor',
        LDH: 'LDH', TP: 'Total Protein'
      }[key] || key;
      return `${turkceKey} girmeniz, ilgili hastalık gruplarının değerlendirilmesi için daha sağlıklı sonuç verir.`;
    });

    return suggestionMessages;
  };

  // ----- Yorumlama (Java kedi mantığı) -----
  const interpret = (statuses) => {
    const findings = [];

    const has = (key, ...statusList) => {
      const s = statuses[key];
      return s && statusList.includes(s);
    };

    // KARACİĞER (liver)
    if ((has('ALT', 'Yüksek') || has('AST', 'Yüksek')) &&
        ( (has('TG', 'Yüksek', 'Düşük')) || (has('CHOL', 'Yüksek', 'Düşük')) )) {
      findings.push({ title: 'Karaciğer', msg: 'Hepatit şüphesi.', level: 'warning' });
    }
    if ((has('ALT', 'Yüksek') || has('AST', 'Yüksek')) && has('ALB', 'Düşük')) {
      findings.push({ title: 'Karaciğer', msg: 'Hepatit olasılığı yüksek.', level: 'warning' });
    }
    if (has('ALP', 'Yüksek') || has('GGT', 'Yüksek')) {
      findings.push({ title: 'Karaciğer', msg: 'Kolesistit şüphesi.', level: 'warning' });
    }
    if (has('TBIL', 'Yüksek') && has('ALT', 'Normal') && has('AST', 'Normal')) {
      findings.push({ title: 'Karaciğer', msg: 'Prehepatik ikterus şüphesi.', level: 'warning' });
    }

    // BÖBREK (kidney)
    if (has('BUN', 'Yüksek') || has('CREA', 'Yüksek')) {
      findings.push({ title: 'Böbrek', msg: 'Böbrek Yetmezliği şüphesi.', level: 'danger' });
    }
    if (has('BUN', 'Yüksek') && has('CREA', 'Yüksek') && has('CA', 'Düşük') && has('PHOS', 'Yüksek')) {
      findings.push({ title: 'Böbrek', msg: 'Kronik Böbrek Yetmezliği şüphesi.', level: 'danger' });
    }

    // SİNDİRİM (digestive)
    if (has('LDH', 'Yüksek')) {
      findings.push({ title: 'Sindirim', msg: 'LDH Yüksekliği: Doku hasarı veya torsiyon olabilir.', level: 'warning' });
    }

    // VİRAL (sadece kedi için FIP)
    if (species === 'cat') {
      const alb = parseFloat(values.ALB);
      const tp = parseFloat(values.TP);
      if (!isNaN(alb) && !isNaN(tp) && tp > alb) {
        const ratio = alb / (tp - alb);
        if (ratio < 0.7) {
          findings.push({ title: 'Viral', msg: `FIP şüphesi (A/G = ${ratio.toFixed(2)}).`, level: 'warning' });
        }
      }
    }

    return findings;
  };

  const handleAnalyze = () => {
    const newSuggestions = generateSuggestions();
    setSuggestions(newSuggestions);

    const statuses = getStatuses();
    const findings = interpret(statuses);

    if (findings.length === 0) {
      setReport([{ title: 'Bilgi', msg: 'Belirgin bir eşleşme bulunamadı.', level: 'info' }]);
    } else {
      setReport(findings);
    }
  };

  // ----- Paneller -----
  const panels = [
    { name: 'Hepatobiliyer', keys: ['ALT', 'AST', 'ALP', 'GGT', 'TBIL', 'CHOL', 'TG'] },
    { name: 'Renal & Metabolik', keys: ['BUN', 'CREA', 'GLU', 'AMYL', 'LIPA', 'LDH'] },
    { name: 'Proteinler', keys: ['TP', 'ALB'] },
    { name: 'Elektrolitler', keys: ['CA', 'PHOS', 'NA', 'K', 'CL'] },
  ];

  return (
    <div style={containerStyle}>
      <h2 style={{ textAlign: 'center', color: '#2c3e50' }}>🩺 Biyokimya Analizörü</h2>

      <div style={speciesToggle}>
        <button
          onClick={() => { setSpecies('dog'); setReport([]); setSuggestions([]); }}
          style={species === 'dog' ? activeBtn : passiveBtn}
        >
          KÖPEK (🐕)
        </button>
        <button
          onClick={() => { setSpecies('cat'); setReport([]); setSuggestions([]); }}
          style={species === 'cat' ? activeBtn : passiveBtn}
        >
          KEDİ (🐈)
        </button>
      </div>

      {panels.map((panel) => (
        <div key={panel.name} style={panelStyle}>
          <h3 style={panelTitleStyle}>{panel.name} Paneli</h3>
          <div style={gridStyle}>
            {panel.keys.map((key) => {
              const item = BIO_DATA[species]?.[key];
              if (!item) return null;
              const status = getStatus(key, values[key]);
              const borderColor =
                status === 'Yüksek' ? '#e74c3c' :
                status === 'Düşük' ? '#3498db' :
                status === 'Normal' ? '#2ecc71' : '#ddd';
              return (
                <div key={key} style={inputCard}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <label style={labelStyle}>{key}</label>
                    <span style={unitStyle}>{item.unit}</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    placeholder={`${item.range[0]}–${item.range[1]}`}
                    value={values[key] || ''}
                    onChange={(e) => handleInput(key, e.target.value)}
                    style={{ ...inputStyle, borderBottom: `3px solid ${borderColor}` }}
                  />
                </div>
              );
            })}
            {panel.name === 'Proteinler' && values.ALB && values.TP && (
              <div style={inputCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <label style={labelStyle}>A/G</label>
                  <span style={unitStyle}>(oran)</span>
                </div>
                <div style={{
                  ...inputStyle,
                  borderBottom: `3px solid ${
                    getStatus('A/G') === 'Yüksek' ? '#e74c3c' :
                    getStatus('A/G') === 'Düşük' ? '#3498db' :
                    getStatus('A/G') === 'Normal' ? '#2ecc71' : '#ddd'
                  }`,
                  backgroundColor: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '10px',
                  boxSizing: 'border-box'
                }}>
                  {(values.ALB / (values.TP - values.ALB)).toFixed(2)}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}

      <button onClick={handleAnalyze} style={analyzeBtn}>
        RAPORU OLUŞTUR
      </button>

      {(suggestions.length > 0 || report.length > 0) && (
        <div style={reportBox}>
          {suggestions.length > 0 && (
            <>
              <h3 style={{ marginTop: 0, color: '#2c3e50' }}>📌 Öneriler</h3>
              {suggestions.map((msg, i) => (
                <div key={i} style={{ ...reportItem, borderLeft: '5px solid #3498db', backgroundColor: '#eef9ff' }}>
                  {msg}
                </div>
              ))}
            </>
          )}

          {report.length > 0 && (
            <>
              <h3 style={{ marginTop: suggestions.length > 0 ? 20 : 0, color: '#2c3e50' }}>📋 Klinik Bulgular</h3>
              {report.map((item, i) => (
                <div
                  key={i}
                  style={{
                    ...reportItem,
                    borderLeft: `5px solid ${
                      item.level === 'danger' ? '#e74c3c' : item.level === 'warning' ? '#f1c40f' : '#3498db'
                    }`,
                  }}
                >
                  <strong>{item.title}:</strong> {item.msg}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// --- Stiller (iOS Çentik ve Mobil Uyumlu Güncellemeler Eklendi) ---
const containerStyle = { 
    padding: '15px', 
    maxWidth: '1000px', 
    margin: '0 auto', 
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    boxSizing: 'border-box',
    width: '100%',
    paddingTop: 'max(15px, env(safe-area-inset-top))', 
    paddingBottom: 'env(safe-area-inset-bottom)'      
};

const speciesToggle = { display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px', width: '100%' };
const activeBtn = { padding: '12px 20px', background: '#2c3e50', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', boxShadow: '0 4px 10px rgba(44,62,80,0.3)', flex: 1, WebkitTapHighlightColor: 'transparent' };
const passiveBtn = { padding: '12px 20px', background: '#ecf0f1', color: '#7f8c8d', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: '0.3s', flex: 1, WebkitTapHighlightColor: 'transparent' };
const panelStyle = { marginBottom: '30px', padding: '15px', backgroundColor: '#f9f9fc', borderRadius: '12px', border: '1px solid #e0e4e8', boxSizing: 'border-box' };
const panelTitleStyle = { marginTop: 0, marginBottom: '15px', color: '#2c3e50', fontSize: '1.1rem', borderBottom: '2px solid #b0bec5', paddingBottom: '5px' };
const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '15px', boxSizing: 'border-box' };
const inputCard = { background: '#fff', padding: '12px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #f1f2f6', boxSizing: 'border-box' };
const labelStyle = { fontWeight: '700', color: '#34495e', fontSize: '13px' };
const unitStyle = { fontSize: '10px', color: '#95a5a6', fontWeight: 'bold' };

const inputStyle = { 
    width: '100%', 
    padding: '10px', 
    marginTop: '8px', 
    border: '1px solid #dfe6e9', 
    borderRadius: '6px', 
    fontSize: '16px', 
    outline: 'none', 
    transition: 'border 0.3s', 
    boxSizing: 'border-box', 
    WebkitAppearance: 'none' 
};

const analyzeBtn = { width: '100%', padding: '16px', marginTop: '10px', background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 15px rgba(39,174,96,0.3)', transition: 'transform 0.2s', WebkitTapHighlightColor: 'transparent', boxSizing: 'border-box' };
const reportBox = { marginTop: '30px', background: '#fff', padding: '20px', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', boxSizing: 'border-box' };
const reportItem = { padding: '12px', background: '#f8f9fa', marginBottom: '12px', borderRadius: '8px', fontSize: '14px', lineHeight: '1.5', color: '#2c3e50', wordBreak: 'break-word' };